import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ClipboardCheck, FileCheck2, ClipboardEdit, Search, CheckCheck,
  ShieldCheck, Wrench, PackageCheck, IndianRupee, PartyPopper, Clock, Radio,
  UserPlus, MapPin, Cpu, FileText, Camera, Banknote, FileCheck, Send, HandCoins, Users,
} from "lucide-react";

// 24-stage canonical lifecycle mirroring backend core/lifecycle.py
export const STAGES = [
  { key: "Lead Created",                  icon: UserPlus,       eta_days: 0,  blurb: "Your enquiry is captured." },
  { key: "Claim Registered",              icon: ClipboardCheck, eta_days: 0,  blurb: "Claim intimation completed." },
  { key: "Policy Verification",           icon: ShieldCheck,    eta_days: 1,  blurb: "Verifying your policy details." },
  { key: "KYC Verification",              icon: FileCheck2,     eta_days: 1,  blurb: "KYC docs being reviewed." },
  { key: "Document Verification",         icon: FileText,       eta_days: 2,  blurb: "Claim documents reviewed." },
  { key: "AI Risk Assessment",            icon: Cpu,            eta_days: 2,  blurb: "AI scoring your claim." },
  { key: "Claim Manager Assigned",        icon: Users,          eta_days: 2,  blurb: "A dedicated manager takes over." },
  { key: "Workshop Assigned",             icon: MapPin,         eta_days: 3,  blurb: "Cashless garage assigned." },
  { key: "Workshop Accepted",             icon: Wrench,         eta_days: 3,  blurb: "Workshop accepted your claim." },
  { key: "Survey Requested",              icon: ClipboardEdit,  eta_days: 3,  blurb: "Surveyor requested from insurer." },
  { key: "Survey Assigned",               icon: ClipboardEdit,  eta_days: 4,  blurb: "Surveyor allocated & scheduled." },
  { key: "Survey Completed",              icon: Search,         eta_days: 5,  blurb: "On-site inspection wrapped." },
  { key: "Estimate Uploaded",             icon: FileCheck,      eta_days: 5,  blurb: "Repair estimate submitted." },
  { key: "Approval Pending",              icon: CheckCheck,     eta_days: 6,  blurb: "Insurer reviewing estimate." },
  { key: "Approval Received",             icon: ShieldCheck,    eta_days: 7,  blurb: "Repair sanction letter issued." },
  { key: "Repair Started",                icon: Wrench,         eta_days: 8,  blurb: "Cashless garage started work." },
  { key: "Supplementary Approval",        icon: CheckCheck,     eta_days: 9,  blurb: "Additional repairs approved." },
  { key: "Repair Completed",              icon: PackageCheck,   eta_days: 11, blurb: "Vehicle ready for delivery." },
  { key: "Invoice Uploaded",              icon: FileText,       eta_days: 12, blurb: "Final invoice uploaded." },
  { key: "Payment Documents Uploaded",    icon: FileCheck,      eta_days: 12, blurb: "Payment docs submitted." },
  { key: "Claim Submitted",               icon: Send,           eta_days: 13, blurb: "Claim submitted to insurer." },
  { key: "Settlement Processing",         icon: HandCoins,      eta_days: 14, blurb: "Insurer processing payment." },
  { key: "Payment Released",              icon: IndianRupee,    eta_days: 15, blurb: "Payment credited to workshop." },
  { key: "Claim Closed",                  icon: PartyPopper,    eta_days: 16, blurb: "You're done. Enjoy the ride." },
];

// Backward-compat: old short flow used "Documents Verified" and "Settlement Processed".
const LEGACY_MAP = {
  "Documents Verified": "Document Verification",
  "Survey Assigned": "Survey Assigned",
  "Settlement Processed": "Settlement Processing",
};

const IDX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

export default function LiveClaimTracker({ claim, timeline = [], compact = false }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const currentRaw = claim?.current_status || "Claim Registered";
  const current = LEGACY_MAP[currentRaw] || currentRaw;
  const reached = Math.max(0, IDX[current] ?? 0);
  const progress = ((reached + 1) / STAGES.length) * 100;

  const entryFor = useMemo(() => {
    const m = new Map();
    timeline.forEach((t) => { if (!m.has(t.status)) m.set(t.status, t); });
    return m;
  }, [timeline]);

  const createdAt = useMemo(() => (claim?.created_at ? new Date(claim.created_at) : new Date()), [claim?.created_at]);
  const totalDays = STAGES[STAGES.length - 1].eta_days;
  const eta = useMemo(() => {
    const d = new Date(createdAt);
    d.setDate(d.getDate() + totalDays);
    return d;
  }, [createdAt, totalDays]);
  const daysToEta = Math.max(0, Math.ceil((eta - now) / 86400000));

  const nextStage = STAGES[Math.min(reached + 1, STAGES.length - 1)];

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="live-claim-tracker">
      {/* Top status band */}
      <div className="bg-gradient-to-r from-[#003DA5] via-[#0050D1] to-[#003DA5] text-white p-5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 animate-shimmer" />
        <div className="relative flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-90 flex items-center gap-1.5">
              <Radio className="w-3 h-3 animate-pulse text-[#FF7A00]" /> Live · {current}
            </div>
            <div className="font-heading font-black text-2xl mt-0.5">{nextStage.key === current ? "Almost there" : `Up next: ${nextStage.key}`}</div>
            <div className="text-sm opacity-90 mt-1">{STAGES[reached].blurb}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-90 flex items-center gap-1.5 justify-end"><Clock className="w-3 h-3" /> ETA</div>
            <div className="font-heading font-black text-2xl">{daysToEta === 0 ? "Today" : `~${daysToEta}d`}</div>
            <div className="text-xs opacity-80">{eta.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-2 rounded-full bg-white/20 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#00A86B] to-[#FF7A00] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            data-testid="tracker-progress"
          />
        </div>
        <div className="mt-1 text-[10px] font-mono opacity-80">{Math.round(progress)}% complete · Stage {reached + 1}/{STAGES.length}</div>
      </div>

      {/* Steps */}
      <ol className={`p-5 ${compact ? "space-y-2" : "space-y-3"}`}>
        {STAGES.map((s, i) => {
          const done = i < reached;
          const active = i === reached;
          const entry = entryFor.get(s.key);
          const Icon = s.icon;
          return (
            <li key={s.key} className="flex items-start gap-3" data-testid={`tracker-step-${i}`}>
              <div className="relative shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                  done
                    ? "bg-[#00A86B] border-[#00A86B] text-white"
                    : active
                    ? "bg-white dark:bg-slate-800 border-[#003DA5] text-[#003DA5]"
                    : "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400"
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                {active && (
                  <span className="absolute -inset-1 rounded-full border-2 border-[#003DA5]/40 animate-pulse-ring pointer-events-none" />
                )}
                {i < STAGES.length - 1 && (
                  <div className={`absolute left-1/2 -translate-x-1/2 top-10 w-0.5 h-6 ${done ? "bg-[#00A86B]" : "bg-slate-200 dark:bg-slate-700"}`} />
                )}
              </div>
              <div className="flex-1 pb-1">
                <div className="flex items-baseline justify-between gap-3">
                  <div className={`text-sm font-bold ${done || active ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}>
                    {s.key}
                  </div>
                  {entry?.at && (
                    <div className="text-[10px] font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">{new Date(entry.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                  )}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {entry?.note || s.blurb}
                </div>
                {active && !entry && (
                  <div className="text-[11px] text-[#FF7A00] font-bold uppercase tracking-widest mt-0.5">In progress</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
